import { create } from 'zustand'
import { runAssignCard, type AssignParams, type AssignWriteRow, type ConfirmOption, type WriteApproval, type WriteQuestion } from '../../api/assignDriverApi'
import { plainRefusalOf } from '../results/refusalWords'
import { UNDO_WINDOW_MS } from './assignModel'
import { useStoredAssignStore } from './storedAssignStore'

/** The truck a load is put on: its id, its label and the names of its drivers. */
export interface AssignTarget {
    truckId: string
    truckLabel: string
    drivers: string[]
}

/** An assign (or an undo) on its way: running, waiting for an answer, refused by a row, or failed. */
export interface PendingAssign {
    phase: 'running' | 'question' | 'refused' | 'failed'
    target: AssignTarget
    params: AssignParams
    question?: WriteQuestion
    /** The row the card refused, with the confirm options it names (its message is never shown). */
    refusal?: AssignWriteRow
    /** A failure's line, the page's own words. */
    message?: string
}

/** A load assigned from the Agent page: the row shows it in place until the undo window closes. */
interface AssignedLoad {
    target: AssignTarget
    undoUntil: number
}

interface AssignStore {
    /** Keyed by load id. */
    pending: Record<string, PendingAssign | undefined>
    assigned: Record<string, AssignedLoad | undefined>
    setPending: (loadId: string, pending: PendingAssign | undefined) => void
    setAssigned: (loadId: string, assigned: AssignedLoad | undefined) => void
}

export const useAssignStore = create<AssignStore>((set) => ({
    pending: {},
    assigned: {},
    setPending: (loadId, pending) => set((state) => ({ pending: { ...state.pending, [loadId]: pending } })),
    setAssigned: (loadId, assigned) => set((state) => ({ assigned: { ...state.assigned, [loadId]: assigned } })),
}))

/** The one line a failure shows when the text names no known refusal, worded for what was tried. The raw text is logged, never rendered. */
const genericFailureOf = (method: AssignParams['method']): string =>
    method === 'unassign' ? "Couldn't undo: something went wrong on our side. Try again." : "Couldn't assign: something went wrong on our side. Try again."

/** The row's truck and drivers as the card wrote them; the picked truck fills what the row does not name. */
function targetOfRow(row: AssignWriteRow, picked: AssignTarget): AssignTarget {
    const truck = row.truck?.replace(/^truck\s+/i, '').trim()
    return {
        truckId: picked.truckId,
        truckLabel: truck || picked.truckLabel,
        drivers: row.drivers?.length ? row.drivers : picked.drivers,
    }
}

/** A failure: a plain sentence when the text names a known refusal, else the generic line with the raw text logged. */
function failure(loadId: string, target: AssignTarget, params: AssignParams, raw: string): void {
    const mapped = plainRefusalOf(raw)
    if (!mapped) console.error(raw)
    useAssignStore.getState().setPending(loadId, { phase: 'failed', target, params, message: mapped ?? genericFailureOf(params.method) })
}

/** The text of a thrown call: the server's own message when it sent one, else the error's. */
function rawErrorText(error: unknown): string {
    const body = (error as { response?: { data?: { message?: unknown } } } | undefined)?.response?.data?.message
    if (typeof body === 'string') return body
    if (error instanceof Error) return error.message
    return String(error)
}

/** Maps the card's answer onto the load's row: done, a question to answer, a refused row, or a failure. */
function applyOutcome(tabId: string, loadId: string, target: AssignTarget, params: AssignParams, outcome: Awaited<ReturnType<typeof runAssignCard>>): void {
    const { setPending, setAssigned } = useAssignStore.getState()
    if (outcome.status === 'question' && outcome.question) {
        setPending(loadId, { phase: 'question', target, params, question: outcome.question })
        return
    }
    // The card's text is for the model: only a mapped sentence is shown from it.
    if (outcome.status !== 'answered') {
        failure(loadId, target, params, outcome.text)
        return
    }
    const rows = outcome.structured.rows ?? []
    const row = rows.find((candidate) => candidate.load_id === loadId) ?? rows[0]
    if (!row) {
        failure(loadId, target, params, outcome.text)
        return
    }
    if (row.outcome === 'updated') {
        const written = params.method === 'assign' ? targetOfRow(row, target) : null
        setPending(loadId, undefined)
        setAssigned(loadId, written ? { target: written, undoUntil: Date.now() + UNDO_WINDOW_MS } : undefined)
        // The row shows the write now, and the chat's stored writes are read again (the write is stored as a tool result).
        useStoredAssignStore.getState().note(tabId, loadId, written)
        void useStoredAssignStore.getState().load(tabId)
        return
    }
    setPending(loadId, { phase: 'refused', target, params, refusal: row })
}

/** One call of the route for `loadId`, its outcome applied to the row. A transport failure shows as a failed row. */
async function send(tabId: string, loadId: string, target: AssignTarget, params: AssignParams, approval?: WriteApproval): Promise<void> {
    useAssignStore.getState().setPending(loadId, { phase: 'running', target, params })
    try {
        applyOutcome(tabId, loadId, target, params, await runAssignCard(tabId, params, approval))
    } catch (error) {
        failure(loadId, target, params, rawErrorText(error))
    }
}

/**
 * Puts the load on the truck. `dispatch` sends the driver the load (the card decides and says what happened). `confirm` is the
 * overdue choice the user made before picking the truck, sent on the assignment as `confirm: [choice]`.
 */
export const startAssign = (tabId: string, loadId: string, target: AssignTarget, dispatch: boolean, confirm?: string): Promise<void> =>
    send(tabId, loadId, target, {
        method: 'assign',
        assignments: [{ load_id: loadId, truck_id: target.truckId, ...(confirm ? { confirm: [confirm] } : {}) }],
        dispatch,
    })

/** Answers the card's question under the state it asked: accept (with confirm) or decline. */
export async function answerAssign(tabId: string, loadId: string, action: 'accept' | 'decline' | 'cancel'): Promise<void> {
    const pending = useAssignStore.getState().pending[loadId]
    if (!pending?.question) return
    const approval: WriteApproval = { requestState: pending.question.requestState, action, ...(action === 'accept' ? { confirm: true } : {}) }
    await send(tabId, loadId, pending.target, pending.params, approval)
}

/** Sends the row again with one of its confirm options (the server's own confirm value), which asks the card's own question for it. */
export function confirmRefusal(tabId: string, loadId: string, option: ConfirmOption): Promise<void> {
    const pending = useAssignStore.getState().pending[loadId]
    if (!pending) return Promise.resolve()
    const assignments = pending.params.assignments.map((row) => (row.load_id === loadId ? { ...row, confirm: [option.confirm] } : row))
    return send(tabId, loadId, pending.target, { ...pending.params, assignments })
}

/**
 * Takes the load off its truck: the row's Undo right after an assign, else the menu's Unassign on the truck the row shows (`shown`),
 * which a reloaded chat knows only from its stored writes. The card always asks for approval, so the row shows that question.
 */
export function undoAssign(tabId: string, loadId: string, shown?: AssignTarget): Promise<void> {
    const target = useAssignStore.getState().assigned[loadId]?.target ?? shown
    if (!target) return Promise.resolve()
    return send(tabId, loadId, target, { method: 'unassign', assignments: [{ load_id: loadId }], part: 'truck' })
}

/** Drops the row's Undo once its window has closed. */
export const expireAssigned = (loadId: string): void => {
    const assigned = useAssignStore.getState().assigned[loadId]
    if (assigned && assigned.undoUntil <= Date.now()) useAssignStore.getState().setAssigned(loadId, undefined)
}
